import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { BsClock } from "react-icons/bs";
import { FiChevronDown } from "react-icons/fi";
import {
  Card,
  ContentWithImage,
  Headline,
  Link,
  List,
  Loading,
  SectionContent,
  SwitchContent,
  Text,
  WidgetCard,
} from "../../../../components";
import { usePaginate, useStaticJsonRequest } from "../../../../hooks";
import { links } from "../../../../config";
import ProximasDatasImg from "../../../../assets/images/consorcio/ilustracao-homem-na-cadeira-de-rodas.svg";
import { ReactComponent as CalendarioIcon } from "../../../../assets/images/icons/zeppelin-calendar-icon.svg";
import * as S from "./ProximasAssembleias.Style";
import { formatSimpleDate, parseISO } from "../../../../utils";

const CalendarioAssembleiaCard = ({ title, calendario }) => {
  const {
    items: visibleAssembleias,
    setList,
    pages,
    page,
    takeFirst,
    takeLast,
  } = usePaginate({
    pageSize: 4,
    infinite: true,
  });
  const datas = useMemo(
    () =>
      Array.isArray(calendario)
        ? calendario
            .map((d) => parseISO(d))
            .filter((d) => d >= new Date()) // pega apenas assemblias futuras
            .slice(0, 12) // pega apenas os 12 ultimas datas agendadas
        : [],
    [calendario]
  );

  const proximaAssembleia = useMemo(() => datas.shift(), [datas]);
  const proximasAssembleias = useMemo(() => [...datas], [datas]);

  const checkClassNames = useMemo(() => {
    const classNameForMore = page > 1 ? "expanded" : "";
    const classNameForMorePages = pages <= 1 ? " disabled" : "";

    return `${classNameForMore}${classNameForMorePages}`;
  }, [page, pages]);

  const handleChangeMoreItems = useCallback(
    (evt) => {
      evt.preventDefault();

      if (page === pages) {
        takeFirst();
      } else {
        takeLast();
      }
    },
    [page, pages, takeFirst, takeLast]
  );

  useEffect(() => {
    setList(proximasAssembleias);
  }, [proximasAssembleias, setList]);

  return (
    <S.CalendarioAssembleiaCard
      as={WidgetCard}
      header={
        <>
          <S.CalendarioAssembleiaCardTitle as={Text} bodyStrong text={title} />
          <S.HeaderContentCard>
            {proximaAssembleia ? (
              <>
                <S.CalendarIcon as={CalendarioIcon} />
                <div>
                  <S.HeaderText as={Text}>Próxima assembleia:</S.HeaderText>
                  <S.DataText as={Text}>
                    {formatSimpleDate(proximaAssembleia)}
                  </S.DataText>
                </div>
              </>
            ) : null}
          </S.HeaderContentCard>
        </>
      }
      footer={
        <S.MoreItensFooter
          as={Link}
          href="#more-items"
          onClick={handleChangeMoreItems}
          disabled={pages <= 1}
          className={checkClassNames}
        >
          <S.MoreItensFooterCard as={Card}>
            <S.MoreItensFooterLabel
              as={Text}
              textType="button"
              reset={{ marginBottom: "0px" }}
              text={page === 1 ? "Ver mais datas" : "Ver menos datas"}
            />
            <S.MoreItensFooterIcon as={FiChevronDown} />
          </S.MoreItensFooterCard>
        </S.MoreItensFooter>
      }
    >
      {visibleAssembleias.length ? (
        <>
          <Text>Próximas datas</Text>
          <S.TimeLine
            as={List}
            listItems={visibleAssembleias}
            forEach={(assembleia) => (
              <S.TimeLineItem>
                <S.TimeLineItemIcon as={BsClock} />
                <Text
                  textSize="sm"
                  as="span"
                  text={formatSimpleDate(assembleia)}
                  reset={{ marginBottom: "0px" }}
                />
              </S.TimeLineItem>
            )}
          />
        </>
      ) : (
        <Text text="Não há assembleias agendadas no momento" />
      )}
    </S.CalendarioAssembleiaCard>
  );
};

CalendarioAssembleiaCard.defaultProps = {
  calendario: [],
};

CalendarioAssembleiaCard.propTypes = {
  title: PropTypes.string.isRequired,
  calendario: PropTypes.arrayOf(PropTypes.string),
};

const ProximasAssembleias = () => {
  const [contentActive, setContentActive] = useState("");

  const { data, loading } = useStaticJsonRequest(
    links.consorcio.calendarioAssembleias
  );

  const { veic, imob } = useMemo(() => ({ ...data }), [data]);

  return (
    <Loading loading={loading}>
      <SectionContent>
        <Headline nivel="2" styleAs="5">
          Datas das próximas assembleias
        </Headline>
        <Text>
          Confira as próximas datas de acordo com o tipo do seu consórcio
        </Text>
        <ContentWithImage
          imgSrc={ProximasDatasImg}
          imgAlt="Homem conferindo próximas datas"
        >
          <S.CalendarioAssembleiaCardSwitch
            as={SwitchContent}
            leftButton="Consórcio Imobiliário"
            rightButton="Consórcio de Veículo"
            leftContent={
              <CalendarioAssembleiaCard
                title="Consórcio Imobiliário"
                calendario={imob}
              />
            }
            rightContent={
              <CalendarioAssembleiaCard
                title="Consórcio Veículos"
                calendario={veic}
              />
            }
            contentActive={contentActive}
            handleChange={(content) => setContentActive(content)}
            noSwithDeskTop
          />
        </ContentWithImage>
      </SectionContent>
    </Loading>
  );
};

export default ProximasAssembleias;
